import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import patientService from '../../../../services/patient.service';
import { showError, showSuccess } from '../../../../utils/ecxSwal';

function formatDisplayPhone(cellNumber) {
    if (!cellNumber) {
        return '';
    }

    const digits = cellNumber.replace(/\D/g, '');
    if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }

    return cellNumber;
}

function SendPatientWelcomeModal({ show, onClose, onSent, clinicId, patient, channel = 'email' }) {
    const isSms = channel === 'sms';
    const [previewHtml, setPreviewHtml] = useState('');
    const [previewMessage, setPreviewMessage] = useState('');
    const [previewSubject, setPreviewSubject] = useState('');
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState(false);
    const [sending, setSending] = useState(false);

    const patientLabel = useMemo(() => {
        if (!patient) {
            return '';
        }

        const name = [patient.firstName, patient.lastName].filter(Boolean).join(' ');
        return patient.preferredName ? `${name} (${patient.preferredName})` : name;
    }, [patient]);

    const recipientLabel = useMemo(() => {
        if (!patient) {
            return '';
        }

        return isSms ? formatDisplayPhone(patient.cellNumber) : patient.email;
    }, [patient, isSms]);

    const loadPreview = useCallback(async () => {
        if (!clinicId || !patient?.id) {
            return;
        }

        setPreviewLoading(true);
        setPreviewError(false);
        setPreviewHtml('');
        setPreviewMessage('');
        setPreviewSubject('');

        try {
            if (isSms) {
                const res = await patientService.previewPatientWelcomeSms(clinicId, patient.id, {});
                setPreviewMessage(res.data.message || '');
            } else {
                const res = await patientService.previewPatientWelcomeEmail(clinicId, patient.id, {});
                setPreviewHtml(res.data.html || '');
                setPreviewSubject(res.data.subject || '');
            }
        } catch (error) {
            console.error('Failed to preview welcome message', error);
            setPreviewError(true);
            showError({
                title: 'Preview Failed',
                text: error?.response?.data?.errorMessage || 'Could not generate the welcome preview.',
            });
        } finally {
            setPreviewLoading(false);
        }
    }, [clinicId, patient?.id, isSms]);

    useEffect(() => {
        if (show && patient) {
            loadPreview();
        }
    }, [show, patient, channel, loadPreview]);

    const handleSend = async () => {
        if (!clinicId || !patient?.id) {
            return;
        }

        setSending(true);

        try {
            if (isSms) {
                await patientService.sendPatientWelcomeSms(clinicId, patient.id, {});
                showSuccess({
                    title: 'SMS Queued',
                    text: `Welcome SMS to ${recipientLabel} has been queued for delivery.`,
                });
            } else {
                await patientService.sendPatientWelcomeEmail(clinicId, patient.id, {});
                showSuccess({
                    title: 'Email Queued',
                    text: `Welcome email to ${recipientLabel} has been queued for delivery.`,
                });
            }

            const refreshedPatient = await patientService.getPatientById(clinicId, patient.id);
            onSent?.(refreshedPatient.data);
            onClose();
        } catch (error) {
            console.error('Failed to send welcome message', error);
            showError({
                title: 'Send Failed',
                text: error?.response?.data?.errorMessage || 'Could not queue the welcome message for sending.',
            });
        } finally {
            setSending(false);
        }
    };

    const hasPreview = isSms ? Boolean(previewMessage) : Boolean(previewHtml);
    const canSend = !previewLoading && !previewError && hasPreview;

    if (!patient) {
        return null;
    }

    return (
        <Modal
            show={show}
            onHide={onClose}
            centered
            size="lg"
            scrollable
            backdrop="static"
            className={isSms ? 'patient-send-sms-modal' : 'patient-send-email-modal'}
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className={`bi ${isSms ? 'bi-chat-dots' : 'bi-emoji-smile'} me-2`} />
                    Send Welcome {isSms ? 'SMS' : 'Email'}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div className={`${isSms ? 'patient-send-sms-modal__meta' : 'patient-send-email-modal__meta'} mb-3`}>
                    <div className={isSms ? 'patient-send-sms-modal__meta-item' : 'patient-send-email-modal__meta-item'}>
                        <span className={isSms ? 'patient-send-sms-modal__meta-label' : 'patient-send-email-modal__meta-label'}>
                            Patient
                        </span>
                        <span className={isSms ? 'patient-send-sms-modal__meta-value' : 'patient-send-email-modal__meta-value'}>
                            {patientLabel}
                        </span>
                    </div>
                    <div className={isSms ? 'patient-send-sms-modal__meta-item' : 'patient-send-email-modal__meta-item'}>
                        <span className={isSms ? 'patient-send-sms-modal__meta-label' : 'patient-send-email-modal__meta-label'}>
                            To
                        </span>
                        <span className={isSms ? 'patient-send-sms-modal__meta-value' : 'patient-send-email-modal__meta-value'}>
                            {recipientLabel}
                        </span>
                    </div>
                </div>

                <div className={isSms ? 'patient-send-sms-modal__preview' : 'patient-send-email-modal__preview'}>
                    {isSms ? (
                        <>
                            <h6 className="mb-3 fw-normal">Preview</h6>
                            <div className="patient-send-sms-modal__phone">
                                <div className="patient-send-sms-modal__phone-notch" />
                                <div className="patient-send-sms-modal__phone-screen">
                                    <div className="patient-send-sms-modal__phone-header">
                                        <span className="patient-send-sms-modal__phone-contact">{patientLabel}</span>
                                        <span className="patient-send-sms-modal__phone-number">{recipientLabel}</span>
                                    </div>
                                    <div className="patient-send-sms-modal__phone-body">
                                        {previewLoading ? (
                                            <div className="patient-send-sms-modal__preview-loading">
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Loading preview...
                                            </div>
                                        ) : previewMessage ? (
                                            <div className="patient-send-sms-modal__message-bubble">
                                                {previewMessage}
                                            </div>
                                        ) : (
                                            <p className="text-muted mb-0 small">No preview available.</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="patient-send-email-modal__preview-header">
                                <h6 className="mb-1 fw-normal">Preview</h6>
                                <span className="text-muted small">
                                    {previewSubject || 'Subject preview'}
                                </span>
                            </div>

                            <div className="patient-send-email-modal__preview-body">
                                {previewLoading ? (
                                    <div className="patient-send-email-modal__preview-loading">
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Loading preview...
                                    </div>
                                ) : previewHtml ? (
                                    <div
                                        className="patient-send-email-modal__preview-html"
                                        dangerouslySetInnerHTML={{ __html: previewHtml }}
                                    />
                                ) : (
                                    <p className="text-muted mb-0 small">No preview available.</p>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onClose} disabled={sending}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    type="button"
                    disabled={sending || !canSend}
                    onClick={handleSend}
                >
                    {sending ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Sending...
                        </>
                    ) : (
                        <>
                            <i className="bi bi-send me-1" />
                            Send {isSms ? 'SMS' : 'Email'}
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default SendPatientWelcomeModal;
